import { useMutation } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState, type MutableRefObject, type ReactNode } from 'react';
import type { SaveT96ResultInput } from '@lims/shared-validation';
import { useTranslation } from 'react-i18next';
import {
  COARSE_AGGREGATE_DISPLAY_PLACES,
  T96_FRACTION_KEYS,
  T96_GRADING_FRACTIONS,
  evaluateT96,
  formatDecimalForDisplay,
  isT96Document,
  type T96Calculation,
  type T96Document,
  type T96FractionKey,
  type T96Grading,
} from '@lims/shared-types';
import { ApiError } from '../../api/client.js';
import { attemptsApi, type Attempt } from '../../api/endpoints.js';
import {
  ActionButton,
  Alert,
  DateTimeField,
  Panel,
  Reading,
  ReadOnlyField,
  TextAreaField,
  TextField,
  useToast,
  fieldGridClass,
} from '../../components/ui/index.js';
import { useSaveFailureFocus } from '../../components/ui/server-form.js';
import { ErrorNotice } from '../../components/ErrorNotice.js';
import {
  AttemptHeaderPanel,
  ENTERED_MARK,
  ProvenanceLegend,
  rolesCovered,
  type StructuredFormHandle,
} from './D2216Form.js';
import {
  BeforeSubmitPanel,
  BenchLayout,
  BenchSection,
  LiveResultsPanel,
  SectionStrip,
  SubSection,
  type BenchStep,
  type CheckItem,
  type LiveValue,
  type SectionState,
} from './bench-parts.js';
import { ResultBlock, SummaryResult } from './specialized-form/index.js';

/**
 * AASHTO T96 — LOS ANGELES ABRASION (decisions D267–D274). Every field is
 * traced to the aggregate workbooks' sheet `7`: the test details (AR21:AR24),
 * the grading table of ten size fractions (rows 22–31), the mass retained on
 * No. 12 (BJ30), and what the server calculates from them — the total (Y32),
 * the mass passing No. 12 (BJ31) and the loss (BJ32). The grading designation
 * is recorded as typed and the table is NOT checked against it (D271); no
 * limit and no pass/fail is shown.
 *
 * The rules are D2216's (D237–D250): entered readings look different from
 * calculated values; a calculation shown while typing is a PREVIEW by the
 * same function the server runs, replaced by the server's stored values once
 * saved; A FAILED SAVE KEEPS EVERYTHING TYPED.
 */

const INFO_FIELDS = ['numberOfSpheres', 'sphereMass', 'revolutions'] as const;
type InfoField = (typeof INFO_FIELDS)[number];

interface FormDraft {
  readonly gradingDesignation: string;
  readonly info: Readonly<Record<InfoField, string>>;
  readonly grading: Readonly<Record<T96FractionKey, string>>;
  readonly retainedMassNo12: string;
  readonly remarks: string;
  readonly startedAt: string | null;
  readonly completedAt: string | null;
}

const text = (value: string | null | undefined) => value ?? '';
const orNull = (value: string) => (value.trim() === '' ? null : value);

function draftFrom(attempt: Attempt, document: T96Document): FormDraft {
  return {
    gradingDesignation: text(document.testInformation.gradingDesignation),
    info: {
      numberOfSpheres: text(document.testInformation.numberOfSpheres),
      sphereMass: text(document.testInformation.sphereMass),
      revolutions: text(document.testInformation.revolutions),
    },
    grading: Object.fromEntries(T96_FRACTION_KEYS.map((key) => [key, text(document.grading[key])])) as Record<
      T96FractionKey,
      string
    >,
    retainedMassNo12: text(document.retainedMassNo12),
    remarks: text(document.remarks),
    startedAt: attempt.generalResult.executionStartedAt,
    completedAt: attempt.generalResult.executionCompletedAt,
  };
}

function gradingOf(current: FormDraft): T96Grading {
  return Object.fromEntries(T96_FRACTION_KEYS.map((key) => [key, orNull(current.grading[key])])) as unknown as T96Grading;
}

/** The form as the API takes it: blank is absent, readings exactly as typed. */
function payloadOf(current: FormDraft): SaveT96ResultInput {
  return {
    executionStartedAt: current.startedAt,
    executionCompletedAt: current.completedAt,
    testInformation: {
      gradingDesignation: orNull(current.gradingDesignation),
      numberOfSpheres: orNull(current.info.numberOfSpheres),
      sphereMass: orNull(current.info.sphereMass),
      revolutions: orNull(current.info.revolutions),
    },
    grading: gradingOf(current),
    retainedMassNo12: orNull(current.retainedMassNo12),
    remarks: orNull(current.remarks),
  };
}

/** The draft still holds exactly the masses the server calculated from. */
function sameMasses(current: FormDraft, document: T96Document): boolean {
  return (
    T96_FRACTION_KEYS.every((key) => current.grading[key].trim() === text(document.grading[key])) &&
    current.retainedMassNo12.trim() === text(document.retainedMassNo12)
  );
}

/** Whether the last refusal named a field under one of these prefixes. */
function namedUnder(errors: ReadonlyMap<string, string>, prefixes: readonly string[]): boolean {
  return [...errors.keys()].some((field) => prefixes.some((prefix) => field === prefix || field.startsWith(`${prefix}.`)));
}

export function LosAngelesAbrasionPanel({
  attempt,
  isEditable,
  onSaved,
  timeZone,
  handle,
  submitError,
  equipment,
  attachments,
  submitAction,
}: {
  readonly attempt: Attempt;
  readonly isEditable: boolean;
  readonly onSaved: (next: Attempt) => Promise<void>;
  readonly timeZone: string | null;
  /** Filled by the panel so the page's Submit can read the current values. */
  readonly handle?: MutableRefObject<StructuredFormHandle | null>;
  /** A refused submission: its fields are marked here, and nothing typed is reset. */
  readonly submitError?: unknown;
  /** The page's equipment section, placed in the bench's section order. */
  readonly equipment?: ReactNode;
  /** The page's attachments, placed after the worksheet. */
  readonly attachments?: ReactNode;
  /** The page's Submit for review, placed beside Save draft. */
  readonly submitAction?: ReactNode;
}) {
  const { t } = useTranslation();
  const { show } = useToast();
  const formRef = useRef<HTMLDivElement>(null);
  const summaryRef = useRef<HTMLDivElement>(null);
  const stored = attempt.structuredResult?.document;
  const document = isT96Document(stored) ? stored : null;
  const [draft, setDraft] = useState<FormDraft | null>(() => (document ? draftFrom(attempt, document) : null));

  // Replaced ONLY when the stored document changes — after a successful save
  // or a reload. An error never resets what was typed.
  const storedStamp = attempt.structuredResult?.updatedAt ?? null;
  useEffect(() => {
    if (document) setDraft(draftFrom(attempt, document));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storedStamp]);

  const save = useMutation({
    mutationFn: (current: FormDraft) => attemptsApi.saveStructured(attempt.id, payloadOf(current)),
    onSuccess: async (next) => {
      await onSaved(next);
      show({ tone: 'success', message: t('attempt.draftSaved') });
    },
  });

  if (handle) handle.current = { currentPayload: () => (draft ? payloadOf(draft) : null) };

  const [lastError, setLastError] = useState<unknown>(null);
  useEffect(() => {
    if (save.error) setLastError(save.error);
  }, [save.error]);
  useEffect(() => {
    if (submitError) setLastError(submitError);
  }, [submitError]);
  useEffect(() => {
    if (save.isSuccess) setLastError(null);
  }, [save.isSuccess]);

  useSaveFailureFocus(lastError, formRef, summaryRef);

  const fieldErrors = useMemo(() => {
    const errors = new Map<string, string>();
    if (lastError instanceof ApiError) {
      for (const detail of lastError.details) {
        if (detail.field && !errors.has(detail.field)) {
          errors.set(detail.field, t(`fieldErrors.${detail.code}`, { defaultValue: t('d2216.invalidValue') }));
        }
      }
    }
    return errors;
  }, [lastError, t]);

  if (!document || !draft) {
    return <Alert tone="danger" title={t('d2216.missingDocument')} />;
  }

  const update = (patch: Partial<FormDraft>) => setDraft((current) => (current ? { ...current, ...patch } : current));
  const errorOf = (field: string) => fieldErrors.get(field);
  const invalid = (field: string) => (errorOf(field) ? { isInvalid: true, error: errorOf(field)! } : {});

  const isServer = sameMasses(draft, document) && document.calculation !== null;
  const preview = evaluateT96({ grading: gradingOf(draft), retainedMassNo12: orNull(draft.retainedMassNo12) });
  const calculation: T96Calculation | null = isServer ? document.calculation : preview.calculation;
  const provenanceLabel = isServer ? t('d2216.calculatedByServer') : t('d2216.previewLabel');
  // Problems the preview sees that no refusal has named yet.
  const issues = preview.issues.filter((issue) => issue.code !== 'reading_required' && !errorOf(issue.field));
  const calculated = (key: keyof T96Calculation, places: number, emphasis = false) => (
    <Reading
      value={calculation ? formatDecimalForDisplay(calculation[key], places) : null}
      provenance="calculated"
      provenanceLabel={provenanceLabel}
      absentLabel={t('d2216.notCalculated')}
      emphasis={emphasis}
    />
  );
  const mass = COARSE_AGGREGATE_DISPLAY_PLACES.mass;
  const source = calculation ? (isServer ? 'server' : 'preview') : 'none';

  // What the bench's strip, live results and checklist show — read from the
  // screen, never a rule of the test: the server decides on save and submit.
  const datesRecorded = Boolean(draft.startedAt && draft.completedAt);
  const instruments = rolesCovered(attempt);

  const steps: BenchStep[] = [
    {
      id: 'bench-info',
      label: t('bench.section.info'),
      state: datesRecorded && !namedUnder(fieldErrors, ['testInformation', 'executionStartedAt', 'executionCompletedAt']) ? 'done' : 'todo',
    },
    ...(equipment
      ? [
          {
            id: 'bench-equipment',
            label: t('bench.section.equipment'),
            state: (instruments.roles.length > 0 ? (instruments.covered ? 'done' : 'todo') : instruments.any ? 'done' : 'none') as SectionState,
          },
        ]
      : []),
    {
      id: 'bench-worksheet',
      label: t('bench.section.worksheet'),
      state: calculation !== null && !namedUnder(fieldErrors, ['grading', 'retainedMassNo12']) ? 'done' : 'todo',
    },
    {
      id: 'bench-environment',
      label: t('bench.section.remarks'),
      state: namedUnder(fieldErrors, ['remarks']) ? 'todo' : 'none',
    },
  ];

  const checks: CheckItem[] = [
    { key: 'dates', ok: datesRecorded, label: t('bench.check.dates') },
    ...(instruments.roles.length > 0 ? [{ key: 'instruments', ok: instruments.covered, label: t('bench.check.instruments') }] : []),
    { key: 'loss', ok: calculation !== null, label: t('bench.check.lossCalculated') },
    ...(fieldErrors.size > 0 ? [{ key: 'refused', ok: false, label: t('bench.check.serverNamed', { count: fieldErrors.size }) }] : []),
  ];

  const live: LiveValue[] = [
    { key: 'totalInitialMass', label: t('t96.calculated.totalInitialMass'), value: calculated('totalInitialMass', mass) },
    { key: 'passingMassNo12', label: t('t96.calculated.passingMassNo12'), value: calculated('passingMassNo12', mass) },
    {
      key: 'lossPercent',
      label: t('t96.calculated.lossPercent'),
      value: calculated('lossPercent', COARSE_AGGREGATE_DISPLAY_PLACES.abrasionLoss, true),
    },
  ];

  return (
    <BenchLayout
      strip={<SectionStrip steps={steps} />}
      main={
        <>
          <AttemptHeaderPanel attempt={attempt} timeZone={timeZone} />

          <div ref={formRef} className="flex flex-col gap-gutter" data-testid="t96-form">
            <BenchSection id="bench-info">
              <Panel title={t('d2216.testInformationTitle')} description={t('t96.testInformationHint')}>
                <div className={fieldGridClass(2)}>
                  <TextField
                    label={t('t96.gradingDesignation')}
                    description={t('t96.gradingDesignationHint')}
                    value={draft.gradingDesignation}
                    onChange={(value) => update({ gradingDesignation: value })}
                    isReadOnly={!isEditable}
                    {...invalid('testInformation.gradingDesignation')}
                  />
                  {INFO_FIELDS.map((field) => (
                    <TextField
                      key={field}
                      label={t(`t96.${field}`)}
                      value={draft.info[field]}
                      onChange={(value) => update({ info: { ...draft.info, [field]: value } })}
                      isReadOnly={!isEditable}
                      textDirection="ltr"
                      endContent={ENTERED_MARK}
                      {...invalid(`testInformation.${field}`)}
                    />
                  ))}
                  <DateTimeField
                    label={t('d2216.dateTested')}
                    value={draft.startedAt}
                    onChange={(value) => update({ startedAt: value })}
                    timeZone={timeZone}
                    isReadOnly={!isEditable}
                    {...invalid('executionStartedAt')}
                  />
                  <DateTimeField
                    label={t('attempt.executionCompletedLabel')}
                    value={draft.completedAt}
                    onChange={(value) => update({ completedAt: value })}
                    timeZone={timeZone}
                    isReadOnly={!isEditable}
                    {...invalid('executionCompletedAt')}
                  />
                </div>
              </Panel>
            </BenchSection>

            {equipment ? <BenchSection id="bench-equipment">{equipment}</BenchSection> : null}

            <BenchSection id="bench-worksheet">
              <div className="flex flex-col gap-gutter">
                <Panel title={t('t96.gradingTitle')} description={t('t96.gradingHint')} actions={<ProvenanceLegend />}>
                  <ol className={fieldGridClass(2)} data-testid="t96-grading">
                    {T96_GRADING_FRACTIONS.map((fraction) => (
                      <li key={fraction.key} className="min-w-0">
                        <TextField
                          label={t('t96.fraction', { passing: fraction.passing, retained: fraction.retained })}
                          value={draft.grading[fraction.key]}
                          onChange={(value) => update({ grading: { ...draft.grading, [fraction.key]: value } })}
                          isReadOnly={!isEditable}
                          textDirection="ltr"
                          endContent={ENTERED_MARK}
                          {...invalid(`grading.${fraction.key}`)}
                        />
                      </li>
                    ))}
                  </ol>
                  {errorOf('grading') ? (
                    <p className="mt-stack text-caption font-medium text-danger-700">{errorOf('grading')}</p>
                  ) : null}
                  <SummaryResult testId="t96-total" source={source}>
                    <ResultBlock label={t('t96.calculated.totalInitialMass')} formula={t('t96.formula.totalInitialMass')}>
                      {calculated('totalInitialMass', mass)}
                    </ResultBlock>
                  </SummaryResult>
                </Panel>

                <Panel title={t('t96.summaryTitle')} description={t('t96.calculatedHint')}>
                  <div className={fieldGridClass(2)}>
                    <TextField
                      label={t('t96.retainedMassNo12')}
                      value={draft.retainedMassNo12}
                      onChange={(value) => update({ retainedMassNo12: value })}
                      isReadOnly={!isEditable}
                      textDirection="ltr"
                      endContent={ENTERED_MARK}
                      {...invalid('retainedMassNo12')}
                    />
                  </div>
                  <SummaryResult testId="t96-calculated">
                    <ResultBlock
                      label={t('t96.calculated.passingMassNo12')}
                      formula={t('t96.formula.passingMassNo12')}
                      testId="t96-passingMassNo12"
                    >
                      {calculated('passingMassNo12', mass)}
                    </ResultBlock>
                    <ResultBlock
                      label={t('t96.calculated.lossPercent')}
                      formula={t('t96.formula.lossPercent')}
                      testId="t96-lossPercent"
                    >
                      {calculated('lossPercent', COARSE_AGGREGATE_DISPLAY_PLACES.abrasionLoss, true)}
                    </ResultBlock>
                  </SummaryResult>
                  <p className="mt-1 text-caption text-default-600">{calculation ? provenanceLabel : t('t96.notCalculatedHint')}</p>
                  {issues.length > 0 ? (
                    <ul className="mt-1 text-caption font-medium text-warning-700" data-testid="t96-issues">
                      {issues.map((issue) => (
                        <li key={`${issue.field}-${issue.code}`}>{t(`fieldErrors.${issue.code}`)}</li>
                      ))}
                    </ul>
                  ) : null}
                </Panel>
              </div>
            </BenchSection>

            <BenchSection id="bench-environment">
              <Panel title={t('d2216.remarks')}>
                <TextAreaField
                  label={t('d2216.remarks')}
                  value={draft.remarks}
                  onChange={(value) => update({ remarks: value })}
                  isReadOnly={!isEditable}
                  rows={3}
                  {...invalid('remarks')}
                />
                <p className="mt-stack text-caption text-default-600">{t('attempt.emptyIsAbsent')}</p>
              </Panel>
            </BenchSection>
          </div>

          {attachments}
        </>
      }
      side={
        <>
          <LiveResultsPanel values={live} emptyLabel={t('bench.liveResultsEmpty')} />
          <BeforeSubmitPanel
            checks={checks}
            {...(isEditable
              ? {
                  actions: (
                    <>
                      <ActionButton priority="secondary" isLoading={save.isPending} onPress={() => save.mutate(draft)}>
                        {t('attempt.saveDraft')}
                      </ActionButton>
                      <span className="flex-1" />
                      {submitAction}
                    </>
                  ),
                }
              : {})}
            summary={
              <div ref={summaryRef} tabIndex={-1}>
                {save.error ? (
                  fieldErrors.size > 0 ? (
                    <Alert tone="danger" title={t('d2216.saveRefusedTitle')} description={t('d2216.saveRefusedBody')} />
                  ) : (
                    <ErrorNotice error={save.error} />
                  )
                ) : null}
              </div>
            }
          />
        </>
      }
    />
  );
}


/**
 * A FROZEN AASHTO T96 RESULT, READ-ONLY — what a reviewer and an approver act
 * on: the test details, every fraction mass entered, the retained mass, and
 * the total, passing mass and loss as the server calculated them.
 */
export function LosAngelesAbrasionRecord({ document }: { readonly document: T96Document }) {
  const { t } = useTranslation();
  const notRecorded = t('common.notRecorded');
  const entered = (value: string | null | undefined) => (
    <Reading value={value ?? null} provenance="entered" provenanceLabel={t('d2216.enteredLabel')} absentLabel={notRecorded} />
  );
  const calculated = (value: string | null | undefined, places: number, emphasis = false) => (
    <Reading
      value={formatDecimalForDisplay(value ?? null, places)}
      provenance="calculated"
      provenanceLabel={t('d2216.calculatedByServer')}
      absentLabel={t('d2216.notCalculated')}
      emphasis={emphasis}
    />
  );
  const info = document.testInformation;
  const used = T96_GRADING_FRACTIONS.filter((fraction) => document.grading[fraction.key] !== null);
  const mass = COARSE_AGGREGATE_DISPLAY_PLACES.mass;

  return (
    <div className="flex flex-col gap-stack" data-testid="t96-record">
      <SubSection title={t('d2216.testInformationTitle')}>
        <div className={fieldGridClass(2)}>
          <ReadOnlyField label={t('t96.gradingDesignation')}>
            <span dir="auto">{info.gradingDesignation ?? notRecorded}</span>
          </ReadOnlyField>
          {INFO_FIELDS.map((field) => (
            <ReadOnlyField key={field} label={t(`t96.${field}`)}>
              <span dir="ltr">{entered(info[field])}</span>
            </ReadOnlyField>
          ))}
        </div>
      </SubSection>

      <SubSection title={t('t96.gradingTitle')}>
        <div className="overflow-x-auto rounded-large border border-default-200">
          <table className="w-full text-start text-body" data-testid="t96-record-table">
            <caption className="sr-only">{t('t96.gradingTitle')}</caption>
            <thead>
              <tr className="border-b border-default-200 bg-default-50 text-caption text-default-700">
                <th scope="col" className="px-3 py-2 text-start font-medium">{t('t96.passing')}</th>
                <th scope="col" className="px-3 py-2 text-start font-medium">{t('t96.retainedOn')}</th>
                <th scope="col" className="px-3 py-2 text-start font-medium">{t('t96.mass')}</th>
              </tr>
            </thead>
            <tbody>
              {used.length === 0 ? (
                <tr>
                  <td colSpan={3} className="px-3 py-2 text-default-600">{t('t96.noGrading')}</td>
                </tr>
              ) : (
                used.map((fraction) => (
                  <tr key={fraction.key} className="border-b border-default-100 last:border-b-0" data-testid="t96-record-row">
                    <td className="px-3 py-2" dir="ltr">{fraction.passing}</td>
                    <td className="px-3 py-2" dir="ltr">{fraction.retained}</td>
                    <td className="px-3 py-2" dir="ltr">{entered(document.grading[fraction.key])}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </SubSection>

      <SubSection title={t('t96.summaryTitle')}>
        <div className={fieldGridClass(2)}>
          <ReadOnlyField label={t('t96.retainedMassNo12')}>
            <span dir="ltr">{entered(document.retainedMassNo12)}</span>
          </ReadOnlyField>
        </div>
        <SummaryResult>
          <ResultBlock label={t('t96.calculated.totalInitialMass')} formula={t('t96.formula.totalInitialMass')}>
            {calculated(document.calculation?.totalInitialMass, mass)}
          </ResultBlock>
          <ResultBlock label={t('t96.calculated.passingMassNo12')} formula={t('t96.formula.passingMassNo12')}>
            {calculated(document.calculation?.passingMassNo12, mass)}
          </ResultBlock>
          <ResultBlock label={t('t96.calculated.lossPercent')} formula={t('t96.formula.lossPercent')}>
            <span data-testid="t96-record-loss">
              {calculated(document.calculation?.lossPercent, COARSE_AGGREGATE_DISPLAY_PLACES.abrasionLoss, true)}
            </span>
          </ResultBlock>
        </SummaryResult>
        <p className="text-caption text-default-600">{t('t96.recordLegend')}</p>
      </SubSection>

      <ReadOnlyField label={t('d2216.remarks')}>
        <span dir="auto" className="whitespace-pre-wrap">{document.remarks ?? notRecorded}</span>
      </ReadOnlyField>
    </div>
  );
}
